"use client";

import Link from "next/link";
import Icon from "@repo/ui/components/ui/icon/icon";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverClose,
} from "@repo/ui/components/ui/popover";
import {
  ProfileAvatar,
  ProfileAvatarProps,
} from "@repo/ui/components/ui/avatar";
import { cn } from "@repo/ui/lib/utils";
import { Session } from "next-auth";
import {
  useNotifications,
  useNotificationsCounter,
  useDeleteNotification,
  useMarkNotificationAsRead,
  useNotificationsSubscription,
  NotificationsIcon,
  Notifications,
} from "@repo/next-shared/components/notifications";
import { useTranslations } from "next-intl";
import NotificationsList from "@repo/next-shared/components/notifications/notifications-list";

export default function NavLogged({
  session,
  children,
  targetHiddenMobile,
}: {
  session: Session;
  children?: React.ReactNode;
  targetHiddenMobile?: boolean;
}) {
  const t = useTranslations("Nav");
  const { data: notifications } = useNotifications("customer");
  const { data: notificationsCounter } = useNotificationsCounter("customer");
  const onDeleteNotification = useDeleteNotification();
  const onMarkAsReadNotification = useMarkNotificationAsRead();
  useNotificationsSubscription(session, "customer");

  return (
    <div className="flex gap-[20px] max-md:gap-[10px] items-center max-md:flex-row-reverse">
      <Link
        className={cn({
          "max-md:hidden": targetHiddenMobile,
        })}
        href="/search"
      >
        <Icon
          icon="Search"
          className="max-md:hidden h-[46px] w-[46px] flex items-center justify-center rounded-full bg-primary-black dark:bg-primary-white *:stroke-primary-white  dark:*:stroke-primary-magenta *:h-[22px] *:w-[22px]"
        />
        <Icon
          icon="SearchCircle"
          className="h-[24px] w-[24px] hidden max-md:block"
        />
      </Link>
      <Popover>
        <PopoverTrigger>
          <ProfileAvatar
            {...(session.user as ProfileAvatarProps)}
            notificationsCounter={notificationsCounter || 0}
          />
        </PopoverTrigger>
        <PopoverContent align="end">
          <div className="divide-y">
            <PopoverClose asChild>
              <Link href="/profile/overview">
                <div className="p-[20px]">{t("go_to_profile")}</div>
              </Link>
            </PopoverClose>
            <PopoverClose asChild className="hidden max-md:block">
              <Link href="/notifications">
                <div
                  className={cn("p-[20px] flex items-center gap-2", {
                    "font-bold text-primary-black": !!notificationsCounter,
                  })}
                >
                  {t("notifications")}
                  {!!notificationsCounter && (
                    <div className="w-2 h-2 bg-primary-magenta rounded-full"></div>
                  )}
                </div>
              </Link>
            </PopoverClose>
            <div className="p-[20px]">{children}</div>
          </div>
        </PopoverContent>
      </Popover>
      <Link href="/profile/wishlist">
        <div
          className={cn(
            "rounded-full h-[46px] w-[46px] flex items-center justify-center border-2 border-primary-black dark:border-primary-white dark:bg-primary-white",
            "max-md:w-[24px] max-md:h-[24px]",
          )}
        >
          <Icon
            className="md:mt-1 h-[25px] w-[28px] max-md:h-[10px] max-md:w-[14px] fill-primary-magenta"
            icon="Heart"
          />
        </div>
      </Link>
      <div className="max-md:hidden h-[46px]">
        <NotificationsIcon counter={notificationsCounter}>
          <Notifications notificationsUrl="/notifications">
            <NotificationsList
              deleteNotification={(id) =>
                onDeleteNotification.mutate({ id, portal: "customer" })
              }
              markAsReadNotification={(id) =>
                onMarkAsReadNotification.mutate({ id, portal: "customer" })
              }
              notifications={notifications?.list || []}
              inline={true}
            />
          </Notifications>
        </NotificationsIcon>
      </div>
    </div>
  );
}
